import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import PropTypes from "prop-types";
import { FlexColumn, FlexRow, TextWrapper, Wrapper } from "../../../constants/common/StyledGlobalConstants";
import CPNotification from "../../../pages/Login/CPNotification";
import { getPageLabel } from "../../../util/cfstPendoUtils";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import { CFST_PENDO_DEFAULT_LABEL, pendoTrack } from "../../../util/pendoUtil";
import { SUPPORT_PHONE_NUMBERS } from "../../../constants/technicianAppointmentConstants";
import moment from "moment";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

export const SlotCard = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: ${(props) => props.width};
  margin-top: 0.5rem;
  padding: 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? "#E3F2FD !important" : "transparent")};
  border: ${(props) => (props.selected ? "2px solid #0065C1" : "1px solid #D8DADA")};

  &:hover {
    background-color: #d8dada;
  }
  &:focus {
    border: 2px solid #006fc1;
    background-color: unset;
  }
`;

const AvailableDates = ({ selectedDate, setSelectedDate, datesWithSlots, fetchSlotsForSelectedDate, setApptTimeDateAndTime, slotsRef }) => {
  const { windowSize, accountNumber, isSourcewellGovernment } = useSelector((state) => state.ACCOUNT);
  const { jobResponseData, isReschedule, jobDetails } = useSelector((state) => state.TECHNICIANAPPOINTMENT);

  const [showNoSlotsNotification, setShowNoSlotsNotification] = useState(!datesWithSlots[0].isAvailable);
  const [announcement, setAnnouncement] = useState("");

  const isTablet = windowSize.width < DESKTOP;
  const isMobile = windowSize.width < TABLET;

  const nextContentRef = useRef(null);

  useEffect(() => {
    if (!selectedDate && datesWithSlots.length > 0) {
      setSelectedDate(datesWithSlots[0].date);
    }
  }, [selectedDate, datesWithSlots, setSelectedDate]);

  // On keyboard accessible to select the available date
  const keyPressSelect = (e, date) => {
    if (e.key === "Enter" || e.key === " ") {
      availableDateHandler(date);
      e.target.blur();
      nextContentRef.current.focus();
    }
  };

  const ROWS = 5;

  const focusElement = (id) => {
    const element = document.getElementById(id);
    if (element) element.focus();
  };

  const isWithinBounds = (index, rows, totalItems, direction) => {
    if (direction === "down") return index + rows < totalItems;
    if (direction === "up") return index - rows >= 0;
    if (direction === "right") return index + 1 < totalItems && (index + 1) % rows !== 0;
    if (direction === "left") return index - 1 >= 0 && index % rows !== 0;
    return false;
  };

  const KEY_TO_DIRECTION = {
    ArrowDown: "down",
    ArrowUp: "up",
    ArrowRight: "right",
    ArrowLeft: "left",
  };

  const handleArrowNavigation = (e, index) => {
    const totalItems = datesWithSlots.length;
    const direction = KEY_TO_DIRECTION[e.key];

    if (direction && isWithinBounds(index, ROWS, totalItems, direction)) {
      const DIRECTION_TO_OFFSET = {
        down: ROWS,
        up: -ROWS,
        right: 1,
        left: -1,
      };
      const offset = DIRECTION_TO_OFFSET[direction];
      focusElement(`slot-card-${index + offset}`);
    }
  };

  // Load the available slots for the selected date in time slots dropdown
  const availableDateHandler = (date) => {
    if (selectedDate.toDateString() !== date.date.toDateString()) {
      setSelectedDate(date.date);
      setShowNoSlotsNotification(!date.isAvailable);
      if (!date.isAvailable) {
        pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | ${getPageLabel()} | No slots availabe`, {
          status: `Selected date: ${moment(date.date).format("M/D/YYYY")}`,
          accountId: accountNumber,
        });
      }
      setApptTimeDateAndTime(null);
      fetchSlotsForSelectedDate(date.date);
      slotsRef.current?.scrollIntoView({
        behavior: "smooth",
      });
      setAnnouncement(`Selected date: ${date.date.toLocaleDateString()}`);
    }
  };

  const getSupportMessage = (isSourcewellGovernment, isReschedule, jobDetails, jobResponseData) => {
    const phoneNumber = isSourcewellGovernment ? SUPPORT_PHONE_NUMBERS.GOVERNMENT : SUPPORT_PHONE_NUMBERS.COMMERCIAL;
    const appointmentId = isReschedule ? jobDetails.id : jobResponseData.id;

    return `For help, call us on ${phoneNumber} and quote this appointment number: ${appointmentId}.`;
  };

  return (
    <FlexColumn marginTop={isTablet ? "1.5rem" : "2rem"} flex={2}>
      <TextWrapper fontSize="1.25rem" fontWeight="700" lineHeight="1.5rem">
        Available dates
      </TextWrapper>
      <TextWrapper fontSize="0.875rem" fontWeight="400" marginTop="1rem !important">
        Availability is shown for <b>10 days</b> from your preferred date.{" "}
      </TextWrapper>
      <FlexRow marginTop={isMobile ? "0.5rem" : "1rem"} justifyContent="space-between">
        {datesWithSlots.map((date, index) => (
          <SlotCard
            id={`slot-card-${index}`}
            width={isMobile ? "100%" : "19%"}
            selected={selectedDate?.toDateString() === date.date.toDateString()}
            key={date.date}
            onClick={() => availableDateHandler(date)}
            onKeyDown={(e) => {
              keyPressSelect(e, date);
              handleArrowNavigation(e, index);
            }}
            tabIndex={0}
            aria-label={`${date.date.toLocaleString("default", { weekday: "short" })} ${date.date.toLocaleString("default", {
              month: "short",
            })} ${date.date.getDate()} ${date.isAvailable ? "Available" : "Unavailable"}`}
          >
            <TextWrapper fontWeight="400" fontSize="0.75rem" color="#6F7171" lineHeight="0.898rem">
              {date.date.toLocaleString("default", { weekday: "short" })}
            </TextWrapper>
            <TextWrapper fontWeight="700" fontSize="0.875rem" marginTop="0.5rem !important">
              {date.date.toLocaleString("default", { month: "short" })} {date.date.getDate()}
            </TextWrapper>
            <FlexRow alignItems="center" marginTop="0.5rem !important" columnGap="0.5rem" flexWrap="nowrap">
              <Wrapper>
                <Wrapper width="0.5rem" height="0.5rem" backgroundColor={date.isAvailable ? "#008331" : "#C44904"} borderRadius="50%"></Wrapper>
              </Wrapper>
              <TextWrapper fontWeight="400" fontSize="11px" lineHeight="13.17px">
                {date.isAvailable ? "Available" : "Unavailable"}
              </TextWrapper>
            </FlexRow>
          </SlotCard>
        ))}
      </FlexRow>
      <div className="sr-only" aria-live="assertive">
        {announcement}
      </div>
      {showNoSlotsNotification && (
        <Wrapper fontWeight="400" fontSize="0.875rem" flex="1" marginTop="1rem">
          <CPNotification isOpen={true} notificationType="cfsterror" closeButton={false} width="auto" height="auto">
            <TextWrapper fontWeight="700">There are no appointments available on your preferred date. Try a different date.</TextWrapper>
            <TextWrapper>{getSupportMessage(isSourcewellGovernment, isReschedule, jobDetails, jobResponseData)}</TextWrapper>
          </CPNotification>
        </Wrapper>
      )}
    </FlexColumn>
  );
};

AvailableDates.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  setSelectedDate: PropTypes.func.isRequired,
  datesWithSlots: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.instanceOf(Date).isRequired,
      isAvailable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  fetchSlotsForSelectedDate: PropTypes.func.isRequired,
  setApptTimeDateAndTime: PropTypes.func.isRequired,
  slotsRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

export default AvailableDates;
